import { Sparkles, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Aviso inline de posible duplicado tras guardar. Permite:
 *  - Cambiar la fecha de la factura recién creada.
 *  - Eliminar el duplicado.
 *  - Mantener ambas facturas.
 */
export default function SoftDuplicateAlert({
  info,
  lang,
  busy,
  dateDraft,
  onDateDraftChange,
  onChangeDate,
  onDelete,
  onKeep,
  onReplace,
  onEdit,
}) {
  if (!info) return null;
  const existing = info.existing || {};
  const created = info.created || {};

  return (
    <div
      className="mt-3 p-4 rounded-lg bg-[var(--status-warning-bg)] border border-[var(--status-warning)]/50 space-y-3"
      data-testid="soft-dup-alert"
    >
      <div className="flex items-start gap-2">
        <Sparkles size={18} className="text-[var(--status-warning)] shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-[var(--status-warning)]">
            {lang === "es" ? "Posible factura duplicada" : "Possible duplicate invoice"}
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {lang === "es"
              ? `Ya existe una factura similar: ${existing.vendor || "?"} · ${existing.date || "?"} · nº ${existing.invoice_number || "?"} · ${existing.amount}${existing.currency ? " " + existing.currency : ""}.`
              : `A similar invoice already exists: ${existing.vendor || "?"} · ${existing.date || "?"} · # ${existing.invoice_number || "?"} · ${existing.amount}${existing.currency ? " " + existing.currency : ""}.`}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            {lang === "es"
              ? "Si en realidad es de otro mes y la fecha se extrajo mal, corrígela aquí:"
              : "If it belongs to a different month and the date was extracted wrong, fix it here:"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          type="date"
          value={dateDraft || created.date || ""}
          onChange={(e) => onDateDraftChange(e.target.value)}
          className="h-9 w-44"
          data-testid="soft-dup-date-input"
        />
        <Button
          onClick={onChangeDate}
          disabled={busy || !dateDraft || dateDraft === created.date}
          className="h-9 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          data-testid="soft-dup-change-date-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : (lang === "es" ? "Cambiar fecha" : "Change date")}
        </Button>
        <Button
          onClick={onDelete}
          disabled={busy}
          variant="outline"
          className="h-9 text-[var(--status-negative)] hover:bg-[var(--status-negative-bg)]"
          data-testid="soft-dup-delete-btn"
        >
          {lang === "es" ? "Eliminar duplicado" : "Delete duplicate"}
        </Button>
        <Button
          onClick={onKeep}
          disabled={busy}
          variant="outline"
          className="h-9"
          data-testid="soft-dup-keep-btn"
        >
          {lang === "es" ? "Mantener ambas" : "Keep both"}
        </Button>
        {onReplace && (
          <Button
            onClick={onReplace}
            disabled={busy}
            variant="outline"
            className="h-9 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10"
            data-testid="soft-dup-replace-btn"
            title={lang === "es"
              ? "Sobrescribe la factura existente con los datos de la nueva y elimina la nueva."
              : "Overwrite the existing invoice with the new data and delete the new one."}
          >
            {lang === "es" ? "Actualizar con la última" : "Update with latest"}
          </Button>
        )}
        {onEdit && (
          <Button
            onClick={onEdit}
            disabled={busy}
            variant="outline"
            className="h-9"
            data-testid="soft-dup-edit-btn"
          >
            {lang === "es" ? "Editar" : "Edit"}
          </Button>
        )}
      </div>
    </div>
  );
}
